// Die Hilfe kommt vom Server, nicht aus diesem Bündel.
//
// Hier stand einmal der ganze Text: zwölf Bereiche, hundert Fragen, mit
// allem, was dieser Server kann. Ein Bündel lädt aber jeder herunter, ob
// angemeldet oder nicht — damit stand die Beschreibung der nicht
// öffentlichen Seiten wörtlich an einer öffentlich erreichbaren Stelle.
//
// Jetzt liegt der Text in backend/routes/hilfe.json und kommt über
// `GET /help/content`, hinter derselben Prüfung wie alles andere. Was
// bleibt, sind die Typen und die Zuordnung Pfad → Bereich; beides verrät
// nichts, was die Adresszeile nicht ohnehin zeigt.

export type HilfeAbschnitt = {
  frage: string;
  antwort: string;
};

export type HilfeEintrag = {
  titel: string;
  /** Ein Satz: was dieser Bereich ist. Steht ganz oben. */
  kurz: string;
  /** Die Handgriffe der Reihe nach — für das, was man zuerst tun will. */
  schritte?: string[];
  /** Überschrift über den Schritten, wenn „Step by step" zu blass ist. */
  schritteTitel?: string;
  abschnitte: HilfeAbschnitt[];
};

/** Was der Server schickt. */
export type HilfeDaten = {
  areas: Record<string, HilfeEintrag>;
  order: string[];
};

/**
 * Der Bereich zu einem Pfad — längster passender Anfang gewinnt.
 *
 * `/git/mein-projekt` gehört zu `/git`, `/sites/blog` zu `/sites`. Was zu
 * gar nichts passt, landet beim Armaturenbrett: lieber die falsche Seite
 * der Hilfe als gar keine.
 */
export function bereichFuerPfad(pfad: string, bekannt: string[]): string {
  let treffer = "/";
  for (const schluessel of bekannt) {
    if (schluessel === "/") continue;
    if ((pfad === schluessel || pfad.startsWith(schluessel + "/"))
        && schluessel.length > treffer.length) {
      treffer = schluessel;
    }
  }
  return treffer;
}

/**
 * Welche Pfade überhaupt eine Hilfe haben.
 *
 * Die Überschrift einer Seite führt in die Hilfe dieser Seite — dafür muss
 * sie vorher wissen, ob es eine gibt, und der Text kommt erst auf Abruf vom
 * Server. Also hier die Schlüssel, ohne den Text dazu: sie verraten nichts,
 * was die Adresszeile nicht ohnehin zeigt.
 *
 * Dieselbe Liste wie `order` in backend/routes/hilfe.json. Ein Test dort
 * hält beide zusammen (TestHilfeBereicheStimmenMitDerOberflaeche).
 */
export const HILFE_BEREICHE = [
  "/", "/calendar", "/mail", "/storage", "/sites", "/git",
  "/lights", "/machine", "/automation", "/reports", "/settings",
] as const;
// Kein "/grades": woher die Noten kommen, weiß der, dem sie gehören. Eine
// Hilfe, die das erklärt, erklärt nichts — sie steht nur im Weg.

/** Ob es zu diesem Pfad eine eigene Hilfe gibt — nicht nur die des
 *  Armaturenbretts, auf der jeder unbekannte Pfad landet. */
export function hatEigeneHilfe(pfad: string): boolean {
  return bereichFuerPfad(pfad, [...HILFE_BEREICHE]) !== "/" || pfad === "/";
}
